/**
 * 旧い形の表（id が STRING。T1a で消した）を読んでいた探索クエリの出力の形。
 *
 * 探索クエリ（検索・一覧・フロー・detail）は契約から撤去し、作り直しの表を読む `Element` 付きの
 * procedure に置き換えた。ここに残るのは、画面（`apps/web/spec-console`）がまだ型として参照して
 * いる出力の形だけで、どの procedure の入出力でもない。画面の参照が外れたら消す。
 *
 * 詳細未投入の Operation（US 骨格が `nodes[].operation` で参照しているだけで本体がまだ書かれて
 * おらず label が空 — 裁定 B-12・決定 ah）と、宙吊り参照（削除済みノードを指したままの宣言）は
 * **異常ではなく通常の状態**として表現する（決定 ab）。したがって `detailLoaded` / `exists` は
 * レスポンスの一級の情報で、空文字ラベルも正当な値である。
 *
 * 本文はどの形も**1 言語に畳んで**持つ。畳んだ結果が空で原文が埋まっている行には
 * `untranslated` が立つ（`UntranslatedSchema`）。空欄を原文で埋めないため、訳の残作業が
 * 画面から読める。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 旧表を読んでいた探索クエリの出力の形のうち、画面がまだ型として参照しているものを残す。
 * @graph-connects none
 */
import { z } from 'zod';
import { AccessModeSchema, ConstraintViolationSchema, DataAccessKindSchema, FlowNodeTypeSchema, GraphSourcedStringSchema, ImplementationKindSchema, IsoDateTimeSchema, MasterDetailTypeSchema, NodeUpdatedAtSchema, NonEmptyStringSchema, OperationNodeKindSchema, ResolvedTermSchema, SpecEdgeOriginSchema, SpecEdgeTypeSchema, SpecNodeTypeSchema, SpgPresenceSchema, SynonymSchema, TermMatchKindSchema, TermOccurrenceSchema, UntranslatedSchema, pageSchema, } from './spec-graph-common.js';
/**
 * 一覧行 1 つに残っている要確認の件数。**重さごとに分けて持つ。**
 *
 * 合計 1 つだけを返していたときは、行の印を見ても自分が消すべきものか分からなかった。
 * 同じ画面を仕様作成者（赤を消す）と実装者（橙を消す）の両方が見るので、合計から重さは
 * 復元できない。一覧で絞り込むにも重さごとの数が要る。
 * @graph-connects none
 */
export const ViolationCountsSchema = z.object({
    /** 仕様が未完成（仕様作成者が消す） */
    error: z.number().int().min(0),
    /** 実装との紐づけが未登録（実装者が消す） */
    warning: z.number().int().min(0),
});
/** 用語集の 1 行。`usageCount` が 0 の Term が監査の削除候補（孤立 Term）になる。 @graph-connects none */
export const TermListItemSchema = z.object({
    termId: GraphSourcedStringSchema,
    label: z.string(),
    description: z.string(),
    untranslated: UntranslatedSchema,
    synonyms: z.array(SynonymSchema),
    usageCount: z.number().int().min(0),
});
/** 旧 `listTerms` の出力。 @graph-connects none */
export const ListTermsOutputSchema = z.object({
    total: z.number().int().min(0),
    items: z.array(TermListItemSchema),
});
/**
 * 完全一致で拾う表記の索引。共通部の `TermSurfaces` をそのまま写した形である。
 *
 * **畳まずに全表記を返す。** リンクの判定は読み手が選んだ言語と無関係で、日本語の本文に
 * 英語の表記が出ることも、その逆もある。表示言語のラベルだけを配ると、別言語で書かれた
 * 箇所がリンクされず、同じ用語が画面ごとに引けたり引けなかったりする。
 *
 * `synonyms` を分けて持つのは、揺れ表記で当たったことを画面が区別できるようにするためで
 * ある（正表記への言い換えを促す導線が後から要る）。
 * @graph-connects none
 */
export const TermSurfacesSchema = z.object({
    canonical: GraphSourcedStringSchema,
    all: z.array(NonEmptyStringSchema),
    synonyms: z.array(NonEmptyStringSchema),
});
/**
 * 辞書の 1 件。`termId` が用語詳細へのリンク先、`surfaces` が本文に当てる表記である。
 * @graph-connects none
 */
export const TermDictionaryEntrySchema = z.object({
    termId: GraphSourcedStringSchema,
    label: GraphSourcedStringSchema,
    definition: GraphSourcedStringSchema,
    untranslated: UntranslatedSchema,
    surfaces: TermSurfacesSchema,
});
/**
 * フロー図のレーン 1 本。
 *
 * **US 骨格はレーンを宣言しない**（決定 am）。`laneId` はフローが参照する Operation の `lane`
 * で、この配列はフロー内に操作が現れる**初出順**で導出される。骨格の `lanes[]` とノードの
 * `lane` を廃したのは、同じ事実（誰がその操作を行うか）が操作・ノード・骨格の 3 箇所に
 * 書けて乖離しうる状態を消すためである（制約 11）。`actorId` は `laneId` が解決した Actor
 * ノードで、解決できないのは Actor 未登録・表記揺れのときである。
 * @graph-connects none
 */
export const FlowLaneSchema = z.object({
    laneId: GraphSourcedStringSchema,
    actorId: GraphSourcedStringSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    icon: NonEmptyStringSchema.nullable(),
});
/**
 * フローノードのうち process ノードが参照する Operation の中身。
 *
 * 制御ノード（decision / start / end）は Operation を参照しないため `null` になる。
 * 参照先は `nodes[].operation` が指す独立集約の Operation であり、フロー内の位置識別である
 * `nodeId` とは別物である（決定 ah・ap）。`businessId` は操作が専門化している業務で、
 * フロー図の上で「この操作はどの機能の一実装か」を示す。`detailLoaded: false` は参照先が
 * まだ書かれていない状態で、そのとき `label` は空文字・`businessId` は null になる。
 * @graph-connects none
 */
export const FlowOperationSchema = z.object({
    operationId: GraphSourcedStringSchema,
    /**
     * 操作の種別。`precondition` は start 直後に置かれる「手前で済んだ業務」の参照で、本文・
     * 部品・実装先を持たない（SR-04）。図は破線 + 「前提」の帯で描き分け、詳細ペインは本文の
     * 代わりに参照先の業務を出す。
     */
    kind: OperationNodeKindSchema,
    businessId: NonEmptyStringSchema.nullable(),
    /** 前提の操作が参照する業務。normal の操作では null */
    relatedBusinessId: NonEmptyStringSchema.nullable(),
    /**
     * この節点が指す業務の表示名。**図のノードの主表記がこれである。**
     *
     * normal の操作は `businessId`、前提の操作は `relatedBusinessId` の業務を引いた結果である。
     * 2 つは排他（前提は本文を持たず業務を参照するだけ、normal は逆）なので 1 本で足りる。
     * 欄を 2 本に割ると、読み手は毎回「どちらを見るのか」を種別から導く必要がある。
     *
     * 操作の名前（`label`）ではなく業務名を主表記にするのは、操作の名前がその操作のロジックを
     * 要約した文だからである。要約だけを並べた図からは「どの業務の話か」が読み取れない。
     * 業務名を引けなければ空文字で、そのとき図は従来どおり操作の名前へ倒す。
     */
    businessLabel: z.string(),
    /** 業務名に選んだ言語の訳が入っていないか。主表記に添える印の判定に使う。 */
    businessUntranslated: UntranslatedSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    domain: NonEmptyStringSchema.nullable(),
    detailLoaded: z.boolean(),
    violationCounts: ViolationCountsSchema,
});
/**
 * フローノード 1 個。
 *
 * `lane` は**導出値**で、参照する Operation の `lane` である（決定 am・ap）。制御ノード
 * （decision / start / end）は操作を参照しないため `null` になり、参照先の Operation が
 * まだ書かれていない process ノードでも `null` になる。骨格がレーンを宣言していた旧モデルと
 * 違い、ここは読取専用の写しである。
 * @graph-connects none
 */
export const FlowNodeSchema = z.object({
    nodeId: GraphSourcedStringSchema,
    type: FlowNodeTypeSchema,
    lane: GraphSourcedStringSchema.nullable(),
    label: z.string(),
    untranslated: UntranslatedSchema,
    operation: FlowOperationSchema.nullable(),
});
/** フローの遷移 1 本。`id` は骨格の並び順から導出した安定 ID（描画のキー）。 @graph-connects none */
export const FlowTransitionSchema = z.object({
    id: NonEmptyStringSchema,
    from: GraphSourcedStringSchema,
    to: GraphSourcedStringSchema,
    condition: NonEmptyStringSchema.nullable(),
    untranslated: UntranslatedSchema,
});
/**
 * ストーリーが達成する業務 1 件（導出値。SR-04）。
 *
 * 完了点（normal の操作の節点から直接 end へ入る遷移）の直前の操作が専門化する業務である。
 * 宣言はしない — UserStory → Business の直接エッジは持たず、骨格から毎回導く。
 * @graph-connects none
 */
export const AchievedBusinessRefSchema = z.object({
    businessId: GraphSourcedStringSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
});
/** 業務を達成する / 前提として参照するストーリー 1 件（導出値）。 @graph-connects none */
export const StoryRefSchema = z.object({
    userStoryId: GraphSourcedStringSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
});
/** 旧 `getUserStoryFlow` の出力。 @graph-connects none */
export const GetUserStoryFlowOutputSchema = z.object({
    userStoryId: GraphSourcedStringSchema,
    label: z.string(),
    description: z.string(),
    untranslated: UntranslatedSchema,
    epic: z.object({
        epicId: GraphSourcedStringSchema,
        label: z.string(),
        untranslated: UntranslatedSchema,
    }),
    updatedAt: NodeUpdatedAtSchema,
    // 骨格の宣言ではなく、参照する Business の lane をフロー内の初出順に並べた導出値（決定 am）
    lanes: z.array(FlowLaneSchema),
    nodes: z.array(FlowNodeSchema),
    transitions: z.array(FlowTransitionSchema),
    /** このストーリーが達成する業務（導出値。初出順）。0 件は制約 27 の対象 */
    achievedBusinesses: z.array(AchievedBusinessRefSchema),
    violations: z.array(ConstraintViolationSchema),
});
/**
 * 利用テーブル 1 件。`qualifiedName` が db-graph への横断 JOIN キーで、`<schema>.<table>` の
 * 2 段である（db-graph の Table ノードの `qualifiedName` と同じ形）。段を足すと画面に出た
 * テーブル名が db-graph 側の名前と一致せず、そこから辿れなくなる。
 * @graph-connects none
 */
export const UsedTableSchema = z.object({
    schema: GraphSourcedStringSchema,
    table: GraphSourcedStringSchema,
    qualifiedName: GraphSourcedStringSchema,
    origin: SpecEdgeOriginSchema,
});
/**
 * 利用カラム 1 件。
 *
 * `accessMode` の `null` は**宣言から読み出せていない**ことを表す。入力スキーマ
 * （`usesColumnSchema`）は `read` / `write` を必須にしているが、読取が相手にするのは
 * 保存済みの宣言であり、スキーマを通っていない値や語彙外の値がありうる。そこで `read` /
 * `write` のどちらかを既定として埋めると、DB への書き込みの有無という**取り違えると
 * 影響の大きい事実**を BFF が捏造することになる。読めていないことをそのまま渡す。
 * @graph-connects none
 */
export const UsedColumnSchema = z.object({
    column: GraphSourcedStringSchema,
    accessMode: AccessModeSchema.nullable(),
    via: GraphSourcedStringSchema,
    qualifiedName: GraphSourcedStringSchema,
    origin: SpecEdgeOriginSchema,
});
/**
 * Operation の本文。詳細未投入なら 3 配列とも空になる。
 *
 * `dataAccess` はその 0 件が**何を意味するか**を分ける。`"none"` なら「DB を触らないと宣言
 * した」、`null` なら「宣言そのものが無い」（判別子が必須になる前に書かれた操作、または詳細
 * 未投入）である。これを載せないと、画面は両者を同じ空欄として出すことになる。
 *
 * `noDatabaseAccessReason` は `dataAccess: "none"` のときだけ入る。理由を書かせる目的は監査と
 * レビューが「本当に触らないのか」を人手で確かめることなので、読取に載せない限り必須化が
 * 形だけになる。
 * @graph-connects none
 */
export const OperationBodySchema = z.object({
    dataAccess: DataAccessKindSchema.nullable(),
    noDatabaseAccessReason: GraphSourcedStringSchema.nullable(),
    usesTable: z.array(UsedTableSchema),
    usesColumn: z.array(UsedColumnSchema),
});
/**
 * 業務ロジック 1 件（決定 aq）。
 *
 * `operations` はこの規則が実現する操作で、その業務を `SPECIALIZES_BUSINESS` で指している
 * 操作の部分集合であり（制約 14）、逆にその操作は必ずどれかの規則に現れる（制約 15）。
 * `step` は業務ロジックの処理順（1 から連番）で、`id` は挿入・並べ替えでも変わらない同一性である。
 * 並べ替えキーの優先度は `rule` の本文に書かれる（判断 12）。
 * @graph-connects none
 */
export const BusinessLogicRuleSchema = z.object({
    id: GraphSourcedStringSchema,
    step: z.number().int(),
    rule: GraphSourcedStringSchema,
    untranslated: UntranslatedSchema,
    operations: z.array(GraphSourcedStringSchema),
});
/** 帰属先 1 件。`derivedVia` は直接エッジを持たない帰属（Epic）の導出経路。 @graph-connects none */
export const BelongsToRefSchema = z.object({
    id: GraphSourcedStringSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    origin: SpecEdgeOriginSchema,
    derivedVia: NonEmptyStringSchema.nullable(),
});
/**
 * 接地宣言（`implementedBy`）。単数必須（決定 aa・原則 1）で、未宣言なら `null`。
 *
 * `spgPresence` が `never-existed` なら実装待ち、`removed` なら宙吊りである。健全性の
 * ラベルは `deriveGroundingStatus` が決めるため、この応答には持たせない。
 *
 * `spgPresence` の `null` は **SPG 索引と突き合わせていない**ことを表す（3 値のどれでもない
 * 第 4 の状態ではなく、判定そのものが行われていない）。SPG 側の境界カタログが未接続の
 * 供給元では突合ができず、そこで `present` を返すと「実装がある」と嘘をつき、
 * `never-existed` を返すと実装済みの業務が実装待ちに化ける。突合していない事実を
 * そのまま渡し、UI は「未照合」として描く。
 * @graph-connects none
 */
export const ImplementationRefSchema = z.object({
    kind: ImplementationKindSchema,
    ref: GraphSourcedStringSchema,
    edgeType: SpecEdgeTypeSchema,
    spgPresence: SpgPresenceSchema.nullable(),
    lastSeenAt: IsoDateTimeSchema.nullable(),
    removedBy: NonEmptyStringSchema.nullable(),
    origin: SpecEdgeOriginSchema,
});
/**
 * 操作が触る Component 1 件（所属 Screen 併記）。
 *
 * 宣言元は **Operation 側の単数フィールド `component`**（決定 af・ap）で、
 * `OPERATED_BY_COMPONENT` の終点である。宣言が単数フィールドなので（制約 13）これは配列では
 * なく単数で返る（決定 ak）。
 *
 * `exists: false` は削除済み部品を指したままの残存参照（制約 4 の宙吊り）で、UI はリンク切れ
 * として**操作側**に描く（決定 al）。そのとき部品の表示名・所属画面は解決できないため null に
 * なる。旧モデルでは参照の向きが部品 → 業務だったため、この宙吊りは画面側に出ていた。
 * @graph-connects none
 */
export const OperatingComponentSchema = z.object({
    componentId: GraphSourcedStringSchema,
    label: z.string().nullable(),
    untranslated: UntranslatedSchema,
    screenId: GraphSourcedStringSchema.nullable(),
    screenLabel: z.string().nullable(),
    pathPattern: GraphSourcedStringSchema.nullable(),
    transitionTo: z.array(GraphSourcedStringSchema),
    exists: z.boolean(),
    origin: SpecEdgeOriginSchema,
});
/**
 * この操作が属する UserStory 1 件（所属 Epic 併記）。
 *
 * 操作は (UserStory, 業務) の組で一意なので、属するユーザーストーリーはちょうど 1 つに
 * 定まる（決定 ap）。`BELONGS_TO_USER_STORY` の終点である。
 *
 * Epic はこの UserStory 経由でしか導出できない — 操作自身は Epic への経路を持たない。
 * @graph-connects none
 */
export const IncludingUserStorySchema = z.object({
    userStoryId: GraphSourcedStringSchema,
    label: z.string(),
    epicId: GraphSourcedStringSchema,
    epicLabel: z.string(),
    untranslated: UntranslatedSchema,
    origin: SpecEdgeOriginSchema,
});
/** 操作を検証するテストステップ 1 件。 @graph-connects none */
export const VerifyingTestStepSchema = z.object({
    testId: GraphSourcedStringSchema,
    testLabel: z.string(),
    scenarioId: GraphSourcedStringSchema,
    stepId: GraphSourcedStringSchema,
    stepDescription: z.string(),
    untranslated: UntranslatedSchema,
    origin: SpecEdgeOriginSchema,
});
/**
 * 業務詳細に載る操作 1 件。
 *
 * v2 の業務詳細が持っていた「文脈に依存する中身」をそのまま引き受ける（決定 ap）。業務は
 * 登場するユーザーストーリーの数だけ操作を持つので、業務詳細はこの形の配列を返す。
 * @graph-connects none
 */
export const BusinessOperationDetailSchema = z.object({
    operationId: GraphSourcedStringSchema,
    label: z.string(),
    description: z.string(),
    untranslated: UntranslatedSchema,
    detailLoaded: z.boolean(),
    updatedAt: NodeUpdatedAtSchema,
    userStory: IncludingUserStorySchema.nullable(),
    body: OperationBodySchema,
    belongsTo: z.object({ actor: BelongsToRefSchema }),
    // 操作が宣言する側の 2 つ。どちらも単数で、`operatedByComponent` は画面を持たない操作
    // （バッチ・イベント）で null になる（決定 af）。逆引きではないため `referencedBy` には
    // 置かない — 向きが操作 → 部品だからである。
    operatedByComponent: OperatingComponentSchema.nullable(),
    implementedBy: ImplementationRefSchema.nullable(),
    referencedBy: z.object({
        verifiedByTestSteps: z.array(VerifyingTestStepSchema),
    }),
    violations: z.array(ConstraintViolationSchema),
});
/**
 * 旧 `getBusinessDetail` の出力。
 *
 * v3 の業務が自分で持つのは **ドメインと業務ロジックだけ**である（決定 ap）。文脈ごとの中身は
 * `operations` の各要素が持つ。1 業務 = 1 画面のまま操作を並べるのは、業務ロジックが操作間の
 * 裁定を書く記述であり、規則と操作を別画面に割ると読み手が突き合わせを頭の中でやることに
 * なるためである。
 * @graph-connects none
 */
export const GetBusinessDetailOutputSchema = z.object({
    businessId: GraphSourcedStringSchema,
    label: z.string(),
    description: z.string(),
    untranslated: UntranslatedSchema,
    previousId: NonEmptyStringSchema.nullable(),
    detailLoaded: z.boolean(),
    updatedAt: NodeUpdatedAtSchema,
    belongsTo: z.object({ domain: BelongsToRefSchema.nullable() }),
    logic: z.array(BusinessLogicRuleSchema),
    operations: z.array(BusinessOperationDetailSchema),
    terms: z.array(ResolvedTermSchema),
    /**
     * この業務を完了点で達成するストーリー（導出値。SR-04）。0 件でも制約 28 の違反とは限らない
     * — 制約 28 はどこかのストーリーが通常操作として持つかを見る。本体の在り処を読むのに要るので、
     * 画面は隠さず「無し」と出す。
     */
    achievedByStories: z.array(StoryRefSchema),
    /** この業務を前提として参照するストーリー（`PRESUPPOSES_BUSINESS` の逆引き。導出値） */
    presupposedByStories: z.array(StoryRefSchema),
    violations: z.array(ConstraintViolationSchema),
});
/**
 * この Component を触る操作への参照。
 *
 * **逆引きである**（決定 af・ap）。宣言は Operation 側の単数フィールド `component` が持ち、
 * ここは `OPERATED_BY_COMPONENT` の incoming をたどって解決した結果なので、返る操作は必ず
 * 実在する — 部品側に宙吊り（`exists: false`）は起こらない。削除済みの部品を指したままの
 * 宙吊りは操作側に出る（決定 al）。
 *
 * `OPERATED_BY_COMPONENT` は N:1 なので、その逆引きである本参照は**部品 1 件につき複数
 * 返りうる**（`ScreenComponentSchema.operatingOperations` が配列で持つ）。単数で持つと
 * 2 操作目が黙って落ち、落ちたことが読み手にも監査にも分からない。
 * @graph-connects none
 */
export const ComponentOperationRefSchema = z.object({
    operationId: GraphSourcedStringSchema,
    businessId: NonEmptyStringSchema.nullable(),
    /*
     * 操作が属するユーザーストーリー。**操作の中身を読みに行く先を決めるために要る。**
     * 操作は単体の詳細ルートを持たず、中身を出しているのは業務フロー図の詳細ペインなので、
     * 部品の逆引きから操作へ辿る導線は「その操作が属するフロー図を、その操作を開いた状態で
     * 出す」になる（決定 ap）。属性から読めない操作は `null` で、画面はリンクにしない。
     */
    userStoryId: NonEmptyStringSchema.nullable(),
    label: z.string().nullable(),
    untranslated: UntranslatedSchema,
    domain: NonEmptyStringSchema.nullable(),
});
/** Component の操作定義 1 件。 @graph-connects none */
export const ComponentActionSchema = z.object({
    id: GraphSourcedStringSchema,
    trigger: GraphSourcedStringSchema,
    description: NonEmptyStringSchema.nullable(),
    untranslated: UntranslatedSchema,
});
/** Component の入力検証ルール 1 件。 @graph-connects none */
export const ComponentValidationSchema = z.object({
    id: GraphSourcedStringSchema,
    rule: GraphSourcedStringSchema,
    message: NonEmptyStringSchema.nullable(),
    untranslated: UntranslatedSchema,
});
/** 画面 detail が返す部品 1 個。 @graph-connects none */
export const ScreenComponentSchema = z.object({
    componentId: GraphSourcedStringSchema,
    label: z.string(),
    description: z.string(),
    untranslated: UntranslatedSchema,
    /** この部品を触る操作の全件（`OPERATED_BY_COMPONENT` の逆引き）。0 件は違反ではない */
    operatingOperations: z.array(ComponentOperationRefSchema),
    transitionTo: z.array(GraphSourcedStringSchema),
    actions: z.array(ComponentActionSchema),
    validations: z.array(ComponentValidationSchema),
    violations: z.array(ConstraintViolationSchema),
});
/** 流入遷移 1 件（この画面へ遷移してくる部品）。 @graph-connects none */
export const IncomingTransitionSchema = z.object({
    componentId: GraphSourcedStringSchema,
    label: z.string(),
    screenId: GraphSourcedStringSchema,
    screenLabel: z.string(),
    untranslated: UntranslatedSchema,
});
/** 旧 `getScreenDetail` の出力。 @graph-connects none */
export const GetScreenDetailOutputSchema = z.object({
    screenId: GraphSourcedStringSchema,
    label: z.string(),
    pathPattern: GraphSourcedStringSchema,
    description: z.string(),
    untranslated: UntranslatedSchema,
    updatedAt: NodeUpdatedAtSchema,
    /*
     * 導出はレーン名（Actor の ID）までで、表示名は持たない。共通部の画面詳細の読取が
     * 返すのが ID の配列だからで、ここに label / untranslated を置くと BFF が引いていない
     * 値を埋めることになる。実際、固定の空文字と `untranslated: false` を詰めていたため、
     * 画面は「訳が無い」のか「表示名を引いていない」のかを言えなかった。
     * 表示名を出すなら共通部が Actor マスターを引くのが先である。
     */
    accessActors: z.object({
        actors: z.array(z.object({ actorId: GraphSourcedStringSchema })),
        derivedVia: NonEmptyStringSchema,
    }),
    components: z.array(ScreenComponentSchema),
    incomingTransitions: pageSchema(IncomingTransitionSchema),
    violations: z.array(ConstraintViolationSchema),
});
/**
 * 用語を使っているノード 1 件。
 *
 * `occurrenceCount` は 0 を取り、そのとき `firstOccurrence` は `null` になる。`USES_TERM`
 * エッジは在るのに本文に表記が無い状態（Term の改名後にエッジが再導出されていない等）が
 * 実際に起きるためで、その行を落とすと**逆引きの件数がエッジの件数と食い違い、宙吊りの
 * エッジが読取から見えなくなる**。0 件の行こそ監査の対象
 * なので、表せない形にしない。
 * @graph-connects none
 */
export const TermUsageSchema = z.object({
    nodeId: GraphSourcedStringSchema,
    nodeType: SpecNodeTypeSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    matchedAs: z.array(TermMatchKindSchema),
    occurrenceCount: z.number().int().min(0),
    firstOccurrence: TermOccurrenceSchema.nullable(),
});
/** ノード型別の逆引き件数。 @graph-connects none */
export const TermUsageGroupSchema = z.object({
    nodeType: SpecNodeTypeSchema,
    count: z.number().int().min(1),
});
/** 旧 `getTermDetail` の出力。 @graph-connects none */
export const GetTermDetailOutputSchema = z.object({
    termId: GraphSourcedStringSchema,
    label: z.string(),
    definition: z.string(),
    untranslated: UntranslatedSchema,
    synonyms: z.array(SynonymSchema),
    updatedAt: NodeUpdatedAtSchema,
    usedBy: pageSchema(TermUsageSchema).extend({
        groupedByNodeType: z.array(TermUsageGroupSchema),
    }),
    violations: z.array(ConstraintViolationSchema),
});
/**
 * マスターを参照しているノード 1 件。
 *
 * `parentQualifiedName` は参照元 qualifiedName の親（Component なら所属 Screen、UserStory なら
 * 所属 Epic）である。**Business と Operation は親を持たないため null になる**（決定 ae・ap —
 * 独立集約になり ID に所属を含まなくなった）。Epic の逆引きは UserStory が並ぶため `domain` は
 * null になる。
 * @graph-connects none
 */
export const MasterReferenceSchema = z.object({
    nodeId: GraphSourcedStringSchema,
    nodeType: SpecNodeTypeSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
    origin: SpecEdgeOriginSchema,
    parentQualifiedName: GraphSourcedStringSchema.nullable(),
    domain: NonEmptyStringSchema.nullable(),
});
/**
 * 逆引きの親 qualifiedName 単位のグルーピング（in 最大 600 を人が読める単位に畳む）。
 *
 * 束ねる鍵は参照元の親のパスで、親を持たないノードは自分自身が鍵になる。決定 ae・ap で
 * Business と Operation の qualifiedName が単一セグメントになったため、それらの参照は 1 件ずつ
 * 別の束になる。US 単位で束ねるには `BELONGS_TO_USER_STORY` を辿る必要があり、この逆引き
 * 1 本では賄えない。
 * @graph-connects none
 */
export const MasterReferenceGroupSchema = z.object({
    groupQualifiedName: GraphSourcedStringSchema,
    count: z.number().int().min(1),
    nodeIds: z.array(GraphSourcedStringSchema),
});
/** 旧 `getMasterDetail` の出力。 @graph-connects none */
export const GetMasterDetailOutputSchema = z.object({
    type: MasterDetailTypeSchema,
    id: NonEmptyStringSchema,
    label: z.string(),
    description: z.string(),
    untranslated: UntranslatedSchema,
    icon: NonEmptyStringSchema.nullable(),
    updatedAt: NodeUpdatedAtSchema,
    referencedBy: pageSchema(MasterReferenceSchema).extend({
        edgeType: SpecEdgeTypeSchema,
        groupedByParent: z.array(MasterReferenceGroupSchema),
    }),
    violations: z.array(ConstraintViolationSchema),
});
/**
 * 名前だけの一覧の 1 行。
 *
 * `nodeId` は詳細への導線に使う qualifiedName である。行に出す値ではない（読み手が見分ける
 * のは名前であって ID ではない）が、押した先を決めるのに要る。
 * @graph-connects none
 */
export const NamedNodeListItemSchema = z.object({
    nodeId: GraphSourcedStringSchema,
    label: z.string(),
    untranslated: UntranslatedSchema,
});
//# sourceMappingURL=spec-graph-explore.js.map